import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Snackbar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  AssignmentReturn as ReturnInvoiceIcon,
} from '@mui/icons-material';
import { Alert } from '@mui/material';
import { useSales, useCreateSale, useUpdateSale, useDeleteSale, useUsers } from '../services/api';

const SalesPage = () => {
  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState('');
  const [open, setOpen] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [saleData, setSaleData] = useState({ userId: '' });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  const { data: users = [] } = useUsers();
  const { data: sales = [], isLoading, refetch } = useSales();
  const createSaleMutation = useCreateSale();
  const updateSaleMutation = useUpdateSale();
  const deleteSaleMutation = useDeleteSale();

  useEffect(() => {
    if (selectedUser) {
      refetch();
    }
  }, [selectedUser, refetch]);

  const handleUserChange = (event) => {
    setSelectedUser(event.target.value);
  };

  const handleOpen = (sale = null) => {
    console.log(sale,"sale",sale)
    if (sale) {
      setEditingSale(sale);
      setSaleData({
        userId: sale.user?.id || '',
        status: sale.status?.toString() || '1',
        code: sale.code || ''
      });
    } else {
      setEditingSale(null);
      setSaleData({ 
        userId: '', 
        status: '1', 
        code: '' 
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingSale(null);
    setSaleData({ userId: '', status: '1', code: '' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSaleData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const mutation = editingSale ? updateSaleMutation : createSaleMutation;
    const mutationData = editingSale 
      ? { id: editingSale.id, ...saleData }
      : saleData;

    mutation.mutate(mutationData, {
      onSuccess: () => {
        showSnackbar(
          `Sale ${editingSale ? 'updated' : 'created'} successfully`,
          'success'
        );
        handleClose();
        refetch();
      },
      onError: (error) => {
        showSnackbar(
          error.response?.data?.message || 'An error occurred',
          'error'
        );
      }
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this sale?')) {
      deleteSaleMutation.mutate(id, {
        onSuccess: () => {
          showSnackbar('Sale deleted successfully', 'success');
          refetch();
        },
        onError: (error) => {
          showSnackbar(
            error.response?.data?.message || 'Failed to delete sale',
            'error'
          );
        }
      });
    }
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">Sales</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
          >
            Add Sale
          </Button>
        </Box>

        {/* <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="user-select-label">Filter by User</InputLabel>
          <Select
            labelId="user-select-label"
            id="user-select"
            value={selectedUser}
            label="Filter by User"
            onChange={handleUserChange}
          >
            <MenuItem value="">
              <em>All Users</em>
            </MenuItem>
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.fullName} ({user.email})
              </MenuItem>
            ))}
          </Select>
        </FormControl> */}

        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sales?.map((sale) => (
                    <TableRow key={sale.id}>
                      <TableCell>{sale.code}</TableCell>
                      <TableCell>{sale.status === '1' ? 'Active' : 'Inactive'}</TableCell>
                      <TableCell>{sale.user?.fullName || 'N/A'}</TableCell>
                      <TableCell>{sale.user?.email || 'N/A'}</TableCell>
                      <TableCell>{sale.user?.phone || 'N/A'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => navigate(`/sales/${sale.id}/return-invoice`)}
                          size="small"
                          color="primary"
                          title="Return invoice"
                        >
                          <ReturnInvoiceIcon />
                        </IconButton>
                        <IconButton onClick={() => handleOpen(sale)} size="small" title="Edit sale">
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <form onSubmit={handleSubmit}>
          <DialogTitle>{editingSale ? `Edit Sale #${editingSale.code}` : 'Add New Sale'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="sale-user-label">User *</InputLabel>
                  <Select
                    labelId="sale-user-label"
                    id="userId"
                    name="userId"
                    value={saleData.userId}
                    label="User *"
                    onChange={handleChange}
                    required
                    disabled={!!editingSale}
                  >
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.fullName} ({user.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="sale-status-label">Status *</InputLabel>
                  <Select
                    labelId="sale-status-label"
                    id="status"
                    name="status"
                    value={saleData.status || '1'}
                    label="Status *"
                    onChange={handleChange}
                    required
                  >
                    <MenuItem value="1">Active</MenuItem>
                    <MenuItem value="0">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={createSaleMutation.isLoading || updateSaleMutation.isLoading}
            >
              {editingSale ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SalesPage;
